import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ArrowRight, AlertCircle } from "lucide-react";
import { useFlow } from "@/flowStore";
import { submitParticipation } from "@/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function FormPage() {
  const navigate = useNavigate();
  const { photo } = useFlow();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!photo.base64) navigate("/capture", { replace: true });
  }, [photo, navigate]);

  const emErr = email.length > 0 && !EMAIL_RE.test(email.trim());

  const canSubmit =
    firstName.trim().length >= 1 &&
    lastName.trim().length >= 1 &&
    EMAIL_RE.test(email.trim()) &&
    consent &&
    !!photo.base64 &&
    !submitting;

  const onSubmit = async () => {
    if (!canSubmit || !photo.base64) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitParticipation({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        photo_base64: photo.base64,
        photo_mime: photo.mime || "image/jpeg",
        consent,
        newsletter,
      });
      navigate("/success", { replace: true });
    } catch (e) {
      setError(e?.message || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="screen" data-testid="form-screen">
      <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 0 }} />
      <div className="form-screen">
        <div className="form-header">
          <button className="icon-btn-light" data-testid="form-back-button" onClick={() => navigate(-1)}>
            <ChevronLeft size={22} />
          </button>
          <div className="thumb-wrap">
            {photo.dataUrl ? <img src={photo.dataUrl} alt="miniature" /> : null}
          </div>
          <div style={{ width: 40 }} />
        </div>

        <div className="form-body fade-up">
          <div className="eyebrow" style={{ color: "#F8D24E" }}>ÉTAPE 3 / 3</div>
          <h1 className="big-title dark">Tes coordonnées</h1>
          <p className="subtitle" style={{ color: "#3A2A22" }}>
            Renseigne tes infos pour valider ta participation au tirage au sort.
          </p>

          <div className="field">
            <label className="field-label">Prénom</label>
            <input
              className="field-input"
              data-testid="first-name-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Marie"
              autoCapitalize="words"
            />
          </div>

          <div className="field">
            <label className="field-label">Nom</label>
            <input
              className="field-input"
              data-testid="last-name-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Dupont"
              autoCapitalize="words"
            />
          </div>

          <div className="field">
            <label className="field-label">E-mail</label>
            <input
              className={`field-input ${emErr ? "err" : ""}`}
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="marie@exemple.ch"
              type="email"
              autoCapitalize="none"
              autoCorrect="off"
              onKeyDown={(e) => e.key === "Enter" && onSubmit()}
            />
            {emErr ? (
              <div className="err-text" data-testid="email-error">
                Adresse e-mail invalide
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="error-banner" data-testid="form-error-banner">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          ) : null}

          <label className="consent-row" data-testid="consent-row">
            <input
              type="checkbox"
              className="consent-checkbox"
              data-testid="consent-checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span className="consent-text">
              J&apos;accepte que ma photo et mes données (prénom, nom, e-mail) soient utilisées par
              Cave de La Côte dans le cadre du concours Stammtisch Challenge.
            </span>
          </label>

          <label className="consent-row" data-testid="newsletter-row">
            <input
              type="checkbox"
              className="consent-checkbox"
              data-testid="newsletter-checkbox"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
            />
            <span className="consent-text">
              J&apos;accepte de recevoir les actualités, les invitations et offres spéciales de Cave
              de La Côte.
              <span className="consent-optional"> (facultatif)</span>
            </span>
          </label>
        </div>

        <div className="form-footer">
          <button
            className="submit-btn"
            data-testid="submit-button"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <div className="spinner" />
            ) : (
              <>
                Participer au concours
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
